import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, X, Layers } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Catálogo de campos disponíveis no construtor visual.
 * `field` mapeia direto pra dotted path em facts no backend.
 * Cobre os 23+ campos solicitados pelo usuário: corte, luzes, fiado, ticket,
 * profissional, unidade, aniversário, tags, etc.
 */
export const FIELD_CATALOG = {
  // ===== Cliente — identidade =====
  "client.name": {
    label: "Cliente · Nome",
    group: "Cliente",
    type: "string",
    ops: ["contains", "==", "is_empty", "is_not_empty"],
  },
  "client.phone": {
    label: "Cliente · Telefone",
    group: "Cliente",
    type: "string",
    ops: ["is_empty", "is_not_empty", "contains"],
  },
  "client.email": {
    label: "Cliente · E-mail",
    group: "Cliente",
    type: "string",
    ops: ["is_empty", "is_not_empty", "contains"],
  },
  "client.instagram": {
    label: "Cliente · Instagram",
    group: "Cliente",
    type: "string",
    ops: ["is_empty", "is_not_empty", "contains"],
  },
  "client.tags": {
    label: "Cliente · Tags",
    group: "Cliente",
    type: "string",
    ops: ["contains", "is_empty", "is_not_empty"],
  },
  "client.notes": {
    label: "Cliente · Observação interna",
    group: "Cliente",
    type: "string",
    ops: ["contains", "is_empty", "is_not_empty"],
  },
  "client.birthday_month": {
    label: "Cliente · Aniversário (mês)",
    group: "Cliente",
    type: "enum",
    options: [
      { value: "01", label: "Janeiro" }, { value: "02", label: "Fevereiro" },
      { value: "03", label: "Março" }, { value: "04", label: "Abril" },
      { value: "05", label: "Maio" }, { value: "06", label: "Junho" },
      { value: "07", label: "Julho" }, { value: "08", label: "Agosto" },
      { value: "09", label: "Setembro" }, { value: "10", label: "Outubro" },
      { value: "11", label: "Novembro" }, { value: "12", label: "Dezembro" },
      { value: "current", label: "Mês atual" },
    ],
    ops: ["==", "in"],
  },
  "client.funnel_status": {
    label: "Cliente · Status no funil",
    group: "Cliente",
    type: "enum",
    options: [
      { value: "cinza", label: "⚪ Sem contato" },
      { value: "amarelo", label: "🟡 Educativa" },
      { value: "laranja", label: "🟠 Convite agenda" },
      { value: "verde", label: "🟢 Agendou" },
      { value: "azul", label: "🔵 Atendida" },
      { value: "vermelho", label: "🔴 Sem resposta" },
    ],
    ops: ["==", "!=", "in", "not_in"],
  },

  // ===== Cliente — histórico/comportamento =====
  "client.days_since_last_haircut": {
    label: "Sem corte há (dias)",
    group: "Histórico do Cliente",
    type: "int",
    ops: ["==", ">=", "<=", ">", "<", "between"],
    unit: "dias",
  },
  "client.days_since_last_highlights": {
    label: "Sem luzes/mechas há (dias)",
    group: "Histórico do Cliente",
    type: "int",
    ops: ["==", ">=", "<=", ">", "<", "between"],
    unit: "dias",
  },
  "client.days_since_last_visit": {
    label: "Sem visitar há (dias)",
    group: "Histórico do Cliente",
    type: "int",
    ops: ["==", ">=", "<=", ">", "<", "between"],
    unit: "dias",
  },
  // iter63: campo GENÉRICO — usuário escolhe QUALQUER serviço + dias.
  // A UI compõe o field como `client.days_since_service.<service_id>` no save.
  "client.days_since_service": {
    label: "Sem [serviço específico] há (dias)",
    group: "Histórico do Cliente",
    type: "service_days",  // widget especial: dropdown de serviço + input de dias
    ops: [">=", ">", "<=", "<", "==", "is_empty", "is_not_empty"],
    unit: "dias",
    hint: "Escolha o serviço e o mínimo de dias sem realizá-lo. 'está vazio' = cliente nunca fez este serviço.",
  },
  "client.last_service_name": {
    label: "Último serviço — nome exato",
    group: "Histórico do Cliente",
    type: "string",
    ops: ["==", "contains", "is_empty"],
  },
  "client.total_visits": {
    label: "Total de visitas",
    group: "Histórico do Cliente",
    type: "int",
    ops: ["==", ">=", "<=", ">", "<", "between"],
  },
  "client.total_spent": {
    label: "Valor total gasto (R$)",
    group: "Histórico do Cliente",
    type: "number",
    ops: ["==", ">=", "<=", ">", "<", "between"],
    unit: "R$",
  },
  "client.avg_ticket": {
    label: "Ticket médio (R$)",
    group: "Histórico do Cliente",
    type: "number",
    ops: ["==", ">=", "<=", ">", "<", "between"],
    unit: "R$",
  },
  "client.has_open_invoice": {
    label: "Tem fiado em aberto?",
    group: "Histórico do Cliente",
    type: "enum",
    options: [{ value: "true", label: "Sim" }, { value: "false", label: "Não" }],
    ops: ["=="],
  },
  "client.has_incomplete_data": {
    label: "Cadastro incompleto?",
    group: "Histórico do Cliente",
    type: "enum",
    options: [{ value: "true", label: "Sim (falta tel/email/etc)" }, { value: "false", label: "Não" }],
    ops: ["=="],
  },

  // ===== FASE 7+ (jul/2026) — campos de Segmentos Inteligentes =====
  "client.birthday_this_month": {
    label: "🎂 Aniversariante deste mês?",
    group: "Segmentos rápidos",
    type: "enum",
    options: [{ value: "true", label: "Sim" }, { value: "false", label: "Não" }],
    ops: ["=="],
  },
  "client.is_active_client": {
    label: "Cliente ativo? (visitou nos últimos 180d)",
    group: "Segmentos rápidos",
    type: "enum",
    options: [{ value: "true", label: "Sim (ativo)" }, { value: "false", label: "Não (inativo)" }],
    ops: ["=="],
  },
  "client.is_first_visit": {
    label: "Primeira visita?",
    group: "Segmentos rápidos",
    type: "enum",
    options: [{ value: "true", label: "Sim" }, { value: "false", label: "Não" }],
    ops: ["=="],
  },
  "client.gender": {
    label: "Sexo",
    group: "Perfil",
    type: "enum",
    options: [
      { value: "F", label: "Feminino" },
      { value: "M", label: "Masculino" },
      { value: "O", label: "Outro" },
      { value: "", label: "Não informado" },
    ],
    ops: ["==", "!=", "in", "not_in"],
  },
  "client.age_range": {
    label: "Faixa etária",
    group: "Perfil",
    type: "enum",
    options: [
      { value: "menor_18", label: "Menor de 18" },
      { value: "18_24", label: "18-24" },
      { value: "25_34", label: "25-34" },
      { value: "35_44", label: "35-44" },
      { value: "45_59", label: "45-59" },
      { value: "60_mais", label: "60+" },
    ],
    ops: ["==", "!=", "in", "not_in"],
  },
  "client.city": {
    label: "Cidade",
    group: "Perfil",
    type: "string",
    entitySource: "cities",  // iter63: dropdown a partir de cidades cadastradas
    ops: ["==", "!=", "in", "not_in", "contains", "is_empty", "is_not_empty"],
  },
  "client.neighborhood": {
    label: "Bairro",
    group: "Perfil",
    type: "string",
    entitySource: "neighborhoods",  // iter63: dropdown de bairros cadastrados
    ops: ["==", "!=", "in", "not_in", "contains", "is_empty", "is_not_empty"],
  },
  "client.tags_search": {
    label: "Tags do cliente",
    group: "Perfil",
    type: "string",
    entitySource: "tags",  // iter63: dropdown de tags cadastradas
    ops: ["contains", "in", "not_in", "is_empty", "is_not_empty"],
  },
  "client.last_service_name_search": {
    label: "Último serviço realizado",
    group: "Histórico do Cliente",
    type: "string",
    entitySource: "services",  // iter63: dropdown de serviços cadastrados
    entityValueField: "name",
    ops: ["==", "!=", "in", "not_in", "contains", "is_empty", "is_not_empty"],
  },

  // ===== Agendamento =====
  "appointment.status": {
    label: "Agendamento · Status",
    group: "Agendamento",
    type: "enum",
    options: [
      { value: "agendado", label: "Agendado" },
      { value: "confirmado", label: "Confirmado" },
      { value: "em_atendimento", label: "Em atendimento" },
      { value: "concluido", label: "Concluído" },
      { value: "cancelado", label: "Cancelado" },
      { value: "nao_compareceu", label: "Faltou (no-show)" },
    ],
    ops: ["==", "!=", "in", "not_in"],
  },
  "appointment.starts_in_hours": {
    label: "Agendamento · Horas até começar",
    group: "Agendamento",
    type: "number",
    ops: ["==", ">=", "<=", ">", "<", "between"],
    unit: "h",
  },
  "appointment.finalized_hours_ago": {
    label: "Agendamento · Horas desde finalização",
    group: "Agendamento",
    type: "number",
    ops: ["==", ">=", "<=", ">", "<", "between"],
    unit: "h",
  },
  "appointment.professional_name": {
    label: "Agendamento · Profissional",
    group: "Agendamento",
    type: "string",
    entitySource: "employees",  // iter63: dropdown com funcionários cadastrados
    entityValueField: "name",
    ops: ["==", "!=", "in", "not_in", "contains"],
  },
  "appointment.service_name": {
    label: "Agendamento · Serviço",
    group: "Agendamento",
    type: "string",
    entitySource: "services",
    entityValueField: "name",
    ops: ["==", "!=", "in", "not_in", "contains"],
  },
  "appointment.payment_method": {
    label: "Agendamento · Forma de pagamento",
    group: "Agendamento",
    type: "enum",
    options: [
      { value: "dinheiro", label: "Dinheiro" },
      { value: "pix", label: "PIX" },
      { value: "credito", label: "Crédito" },
      { value: "debito", label: "Débito" },
      { value: "transferencia", label: "Transferência" },
      { value: "fiado", label: "Fiado" },
    ],
    ops: ["==", "in"],
  },

  // ===== Fiado =====
  "invoice.status": {
    label: "Fiado · Status",
    group: "Fiado/Cobrança",
    type: "enum",
    options: [
      { value: "pendente", label: "Pendente" },
      { value: "parcial", label: "Parcial" },
      { value: "pago", label: "Pago" },
      { value: "cancelado", label: "Cancelado" },
    ],
    ops: ["==", "!=", "in", "not_in"],
  },
  "invoice.days_overdue": {
    label: "Fiado · Dias vencido",
    group: "Fiado/Cobrança",
    type: "int",
    ops: ["==", ">=", "<=", ">", "<", "between"],
    unit: "dias",
  },
  "invoice.remaining": {
    label: "Fiado · Valor em aberto",
    group: "Fiado/Cobrança",
    type: "number",
    ops: [">=", "<=", "==", ">", "<", "between"],
    unit: "R$",
  },

  // ===== Unidade =====
  "client.unit_id": {
    label: "Unidade do cliente",
    group: "Unidade",
    type: "string",
    entitySource: "units",  // iter63: dropdown de unidades cadastradas
    entityValueField: "unit_id",
    ops: ["==", "!=", "in", "not_in", "is_empty", "is_not_empty"],
  },
};

const OP_LABELS = {
  "==": "é igual a",
  "!=": "é diferente de",
  ">": "é maior que",
  ">=": "é maior ou igual a",
  "<": "é menor que",
  "<=": "é menor ou igual a",
  "in": "está em (lista)",
  "not_in": "NÃO está em (lista)",
  "contains": "contém o texto",
  "between": "está entre",
  "is_empty": "está vazio",
  "is_not_empty": "está preenchido",
};

const newAtomic = (field = "client.funnel_status") => {
  const meta = FIELD_CATALOG[field] || { ops: ["=="], type: "string" };
  return {
    field,
    op: meta.ops?.[0] || "==",
    value: meta.type === "enum" ? meta.options?.[0]?.value || "" : "",
  };
};

// iter63: resolve meta permitindo campos dinâmicos como `client.days_since_service.<service_id>`
function getFieldMeta(field) {
  if (FIELD_CATALOG[field]) return FIELD_CATALOG[field];
  if (field && field.startsWith("client.days_since_service.")) {
    return FIELD_CATALOG["client.days_since_service"];
  }
  return null;
}

const newGroup = () => ({ logic: "or", conditions: [newAtomic()] });

const isGroup = (n) => n && typeof n === "object" && "logic" in n && Array.isArray(n.conditions);

// Updates a node at a specific path (array of indices) within the tree.
// Path empty = update root list.
function patchAtPath(list, path, updater) {
  if (path.length === 0) return updater(list);
  const [head, ...rest] = path;
  const copy = list.slice();
  const node = copy[head];
  if (rest.length === 0) {
    copy[head] = updater(node);
  } else if (isGroup(node)) {
    copy[head] = { ...node, conditions: patchAtPath(node.conditions, rest, updater) };
  }
  return copy;
}

function removeAtPath(list, path) {
  if (path.length === 0) return list;
  if (path.length === 1) {
    const copy = list.slice();
    copy.splice(path[0], 1);
    return copy;
  }
  const [head, ...rest] = path;
  const copy = list.slice();
  const node = copy[head];
  if (isGroup(node)) {
    copy[head] = { ...node, conditions: removeAtPath(node.conditions, rest) };
  }
  return copy;
}

export default function ConditionsBuilder({ value, onChange }) {
  const list = Array.isArray(value) ? value : [];
  const { apiCall } = useAuth();
  // iter63: carrega uma vez as opções cadastradas do salão (serviços,
  // funcionários, unidades, cidades, bairros, tags). Passa para o ValueInput
  // via prop `entityOptions` para renderizar dropdowns em vez de texto livre.
  const [entityOptions, setEntityOptions] = useState({
    services: [], employees: [], products: [], units: [],
    cities: [], neighborhoods: [], tags: [],
    funnel_statuses: [], appointment_statuses: [],
  });
  useEffect(() => {
    (async () => {
      try {
        const r = await apiCall("get", "/segments/options");
        setEntityOptions(r.data || {});
      } catch { /* silencioso: fallback para texto livre */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateNode = (path, patch) =>
    onChange(patchAtPath(list, path, (n) => (typeof patch === "function" ? patch(n) : { ...n, ...patch })));
  const removeNode = (path) => onChange(removeAtPath(list, path));
  const appendChild = (path, child) =>
    onChange(patchAtPath(list, path, (n) => ({ ...n, conditions: [...(n.conditions || []), child] })));

  const rows = flattenNodes(list, []);

  return (
    <div className="space-y-2" data-testid="conditions-builder">
      <div className="text-[10px] text-muted-foreground">
        <span className="font-medium">Todas as regras precisam bater</span> para o cliente entrar no segmento.
        {" "}Use um <span className="font-medium">grupo OU</span> quando qualquer uma puder servir.
      </div>

      {list.length === 0 ? (
        <div className="border border-dashed border-amber-300 bg-amber-50 rounded-md p-3 text-center text-[11px] text-amber-900">
          ⚠ <span className="font-medium">Sem regras — toda a base entra.</span> Adicione pelo menos 1 regra para segmentar. Segmentos ativos sem regras são bloqueados no salvar.
        </div>
      ) : (
        <div className="space-y-1">
          {rows.map((row) => {
            const k = row.path.join("-");
            const depthStyle = { marginLeft: `${row.depth * 12}px` };
            if (row.kind === "group-header") {
              return (
                <div key={`g-${k}`} className="border border-amber-300/70 bg-amber-50/40 rounded-md p-2 flex items-center justify-between" style={depthStyle} data-testid={`cb-group-${k}`}>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest">
                    <Layers className="h-3 w-3 text-amber-700" strokeWidth={1.5} />
                    <span className="text-amber-900 font-medium">Grupo</span>
                    <select
                      value={row.node.logic || "or"}
                      onChange={(e) => updateNode(row.path, { logic: e.target.value })}
                      className="h-6 px-1 text-[10px] border border-input rounded bg-background"
                      data-testid={`cb-group-logic-${k}`}
                    >
                      <option value="or">QUALQUER abaixo casar (OU)</option>
                      <option value="and">TODAS abaixo casarem (E)</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-6 text-[10px]"
                      onClick={() => appendChild(row.path, newAtomic())}
                      data-testid={`cb-group-add-${k}`}
                    >
                      <Plus className="h-3 w-3 mr-0.5" strokeWidth={1.5} /> Condição
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={() => removeNode(row.path)}
                      title="Remover grupo"
                      data-testid={`cb-remove-${k}`}
                    >
                      <X className="h-3 w-3" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              );
            }
            // atomic row
            const node = row.node;
            const meta = getFieldMeta(node.field) || { type: "string", ops: Object.keys(OP_LABELS) };
            const validOps = meta.ops || Object.keys(OP_LABELS);
            return (
              <div
                key={`a-${k}`}
                className="flex items-start gap-2 flex-wrap border border-border rounded-md p-1.5 bg-background"
                style={depthStyle}
                data-testid={`cb-atom-${k}`}
              >
                <div className="min-w-[180px] flex-1">
                  <FieldSelect
                    value={node.field.startsWith("client.days_since_service.") ? "client.days_since_service" : node.field}
                    onChange={(newField) => {
                      const m = getFieldMeta(newField) || { type: "string", ops: ["=="] };
                      const newOp = m.ops?.includes(node.op) ? node.op : m.ops?.[0] || "==";
                      updateNode(row.path, {
                        field: newField,
                        op: newOp,
                        value: m.type === "enum" ? m.options?.[0]?.value || "" : "",
                      });
                    }}
                    dataTestId={`cb-field-${k}`}
                  />
                </div>
                <div className="min-w-[140px]">
                  <select
                    value={node.op}
                    onChange={(e) => {
                      const newOp = e.target.value;
                      let v = node.value;
                      if (newOp === "between" && !Array.isArray(v)) v = [0, 0];
                      else if (newOp !== "between" && Array.isArray(v) && newOp !== "in" && newOp !== "not_in") v = v[0] ?? "";
                      else if ((newOp === "in" || newOp === "not_in") && !Array.isArray(v)) v = v ? [v] : [];
                      updateNode(row.path, { op: newOp, value: v });
                    }}
                    className="h-8 w-full px-2 text-xs border border-input rounded-md bg-background"
                    data-testid={`cb-op-${k}`}
                  >
                    {validOps.map((op) => (
                      <option key={op} value={op}>{OP_LABELS[op] || op}</option>
                    ))}
                  </select>
                </div>
                <div className="min-w-[160px] flex-1">
                  <ValueInput
                    meta={meta}
                    op={node.op}
                    value={node.value}
                    onChange={(v) => updateNode(row.path, { value: v })}
                    pathKey={k}
                    entityOptions={entityOptions}
                    fullField={node.field}
                    onFieldChange={(newField) => updateNode(row.path, { field: newField })}
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={() => removeNode(row.path)}
                  title="Remover condição"
                  data-testid={`cb-remove-${k}`}
                >
                  <X className="h-3 w-3" strokeWidth={1.5} />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-[11px]"
          onClick={() => onChange([...list, newAtomic()])}
          data-testid="cb-add-condition"
        >
          <Plus className="h-3 w-3 mr-1" strokeWidth={1.5} /> Condição
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-[11px]"
          onClick={() => onChange([...list, newGroup()])}
          data-testid="cb-add-group"
        >
          <Layers className="h-3 w-3 mr-1" strokeWidth={1.5} /> Grupo (OU)
        </Button>
      </div>
    </div>
  );
}

// Flatten the tree iteratively into render rows so the JSX above stays linear
// (avoids mutual-recursion in JSX that confuses the visual-edits babel plugin).
function flattenNodes(list, basePath) {
  const out = [];
  for (let i = 0; i < list.length; i++) {
    const node = list[i];
    const path = [...basePath, i];
    if (isGroup(node)) {
      out.push({ kind: "group-header", node, path, depth: basePath.length });
      const inner = flattenNodes(node.conditions || [], path);
      for (const r of inner) out.push(r);
    } else {
      out.push({ kind: "atom", node, path, depth: basePath.length });
    }
  }
  return out;
}

function FieldSelect({ value, onChange, dataTestId }) {
  const grouped = useMemo(() => {
    const g = {};
    Object.entries(FIELD_CATALOG).forEach(([k, v]) => {
      g[v.group] = g[v.group] || [];
      g[v.group].push({ key: k, label: v.label });
    });
    return g;
  }, []);
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 w-full px-2 text-xs border border-input rounded-md bg-background"
      data-testid={dataTestId}
    >
      {Object.entries(grouped).map(([groupName, items]) => (
        <optgroup key={groupName} label={groupName}>
          {items.map((it) => (
            <option key={it.key} value={it.key}>{it.label}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

function ValueInput({ meta, op, value, onChange, pathKey, entityOptions = {}, fullField, onFieldChange }) {
  // iter63: campo composto "sem [serviço] há X dias" — dropdown de serviço + dias
  if (meta.type === "service_days") {
    // fullField pode ser "client.days_since_service" ou "client.days_since_service.<service_id>"
    const parts = (fullField || "client.days_since_service").split(".");
    const selectedServiceId = parts.length >= 3 ? parts.slice(2).join(".") : "";
    const services = entityOptions.services || [];
    const setService = (sid) => {
      if (onFieldChange) {
        onFieldChange(sid ? `client.days_since_service.${sid}` : "client.days_since_service");
      }
    };
    return (
      <div className="flex items-center gap-1 flex-wrap">
        <select
          value={selectedServiceId}
          onChange={(e) => setService(e.target.value)}
          className="h-8 px-2 text-xs border border-input rounded-md bg-background min-w-40"
          data-testid={`cb-val-service-${pathKey}`}
        >
          <option value="">— Escolher serviço —</option>
          {services.map((s) => (
            <option key={s.service_id} value={s.service_id}>
              {s.name}{s.active === false ? " (inativo)" : ""}
            </option>
          ))}
        </select>
        {op !== "is_empty" && op !== "is_not_empty" && (
          <div className="flex items-center gap-1">
            <Input
              type="number"
              className="h-8 text-xs w-20"
              value={value ?? ""}
              onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
              placeholder="dias"
              data-testid={`cb-val-service-days-${pathKey}`}
            />
            <span className="text-[10px] text-muted-foreground">dias</span>
          </div>
        )}
      </div>
    );
  }
  if (op === "is_empty" || op === "is_not_empty") {
    return <div className="h-8 px-2 text-[10px] text-muted-foreground flex items-center">— sem valor —</div>;
  }
  if (op === "between") {
    const v = Array.isArray(value) ? value : [0, 0];
    return (
      <div className="flex items-center gap-1">
        <Input
          type="number"
          className="h-8 text-xs"
          value={v[0] ?? ""}
          onChange={(e) => onChange([Number(e.target.value), v[1] ?? 0])}
          data-testid={`cb-val-min-${pathKey}`}
        />
        <span className="text-[10px] text-muted-foreground">e</span>
        <Input
          type="number"
          className="h-8 text-xs"
          value={v[1] ?? ""}
          onChange={(e) => onChange([v[0] ?? 0, Number(e.target.value)])}
          data-testid={`cb-val-max-${pathKey}`}
        />
      </div>
    );
  }
  if (op === "in" || op === "not_in") {
    // iter63: multi-select a partir de entidades cadastradas
    if (meta.entitySource && entityOptions[meta.entitySource]) {
      const src = entityOptions[meta.entitySource] || [];
      const arr = Array.isArray(value) ? value : [];
      const vf = meta.entityValueField;
      const toOpt = (item) => {
        if (typeof item === "string") return { value: item, label: item };
        return { value: vf ? item[vf] : item.name, label: item.name || item.label || String(item) };
      };
      const opts = src.map(toOpt);
      return (
        <div className="flex flex-wrap gap-1 border border-input rounded-md p-1 bg-background min-h-8 max-h-32 overflow-y-auto" data-testid={`cb-val-entity-multi-${pathKey}`}>
          {opts.length === 0 && <span className="text-[10px] text-muted-foreground px-1">(nada cadastrado)</span>}
          {opts.map((opt) => {
            const checked = arr.includes(opt.value);
            return (
              <label
                key={opt.value}
                className={`text-[10px] px-1.5 py-0.5 rounded cursor-pointer border ${checked ? "bg-amber-100 border-amber-400 text-amber-900" : "bg-background border-border"}`}
              >
                <input
                  type="checkbox"
                  className="hidden"
                  checked={checked}
                  onChange={() => {
                    const next = checked ? arr.filter((x) => x !== opt.value) : [...arr, opt.value];
                    onChange(next);
                  }}
                  data-testid={`cb-val-entity-check-${pathKey}-${opt.value}`}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      );
    }
    if (meta.type === "enum") {
      const arr = Array.isArray(value) ? value : [];
      return (
        <div className="flex flex-wrap gap-1 border border-input rounded-md p-1 bg-background min-h-8">
          {meta.options.map((opt) => {
            const checked = arr.includes(opt.value);
            return (
              <label
                key={opt.value}
                className={`text-[10px] px-1.5 py-0.5 rounded cursor-pointer border ${checked ? "bg-amber-100 border-amber-400 text-amber-900" : "bg-background border-border"}`}
              >
                <input
                  type="checkbox"
                  className="hidden"
                  checked={checked}
                  onChange={() => {
                    const next = checked ? arr.filter((x) => x !== opt.value) : [...arr, opt.value];
                    onChange(next);
                  }}
                  data-testid={`cb-val-multi-${pathKey}-${opt.value}`}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      );
    }
    const arr = Array.isArray(value) ? value : [];
    return (
      <Input
        className="h-8 text-xs"
        placeholder="valor1, valor2, ..."
        value={arr.join(", ")}
        onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
        data-testid={`cb-val-list-${pathKey}`}
      />
    );
  }
  // iter63: single-select a partir de entidades cadastradas (==/!=/contains)
  if (meta.entitySource && entityOptions[meta.entitySource] && (op === "==" || op === "!=" || op === "contains")) {
    const src = entityOptions[meta.entitySource] || [];
    const vf = meta.entityValueField;
    const opts = src.map((item) =>
      typeof item === "string"
        ? { value: item, label: item }
        : { value: vf ? item[vf] : item.name, label: item.name || item.label || String(item) }
    );
    return (
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-full px-2 text-xs border border-input rounded-md bg-background"
        data-testid={`cb-val-entity-${pathKey}`}
      >
        <option value="">— Selecione —</option>
        {opts.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    );
  }
  if (meta.type === "enum") {
    return (
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-full px-2 text-xs border border-input rounded-md bg-background"
        data-testid={`cb-val-enum-${pathKey}`}
      >
        {meta.options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    );
  }
  if (meta.type === "number" || meta.type === "int") {
    return (
      <div className="flex items-center gap-1">
        <Input
          type="number"
          className="h-8 text-xs"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          data-testid={`cb-val-num-${pathKey}`}
        />
        {meta.unit ? <span className="text-[10px] text-muted-foreground whitespace-nowrap">{meta.unit}</span> : null}
      </div>
    );
  }
  return (
    <Input
      className="h-8 text-xs"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`cb-val-str-${pathKey}`}
    />
  );
}
